/**
 * MapMark document model (schema v1).
 *
 * Every coordinate is in *image pixel space* (the natural size of the source
 * screenshot). Sizes of markup elements are derived at render time from the
 * image size, so the same document looks identical at every zoom level and in
 * the exported image.
 *
 * This module is shared by the browser app and the Worker. It has no
 * dependencies and validates untrusted JSON strictly.
 */

export const DOC_VERSION = 1 as const;

export type MapKind = 'soils' | 'concrete';

export interface Vec {
  x: number;
  y: number;
}

/** A label position; `null` means "auto-placed by the layout engine". */
export type LabelPos = Vec | null;

/**
 * A stretch of a label's own words with a look of its own (2026-10-28, the inspectors: rich text):
 * bold, italic, underlined, struck through, or in a colour of its own (a key of `TEXT_COLORS` or
 * #rrggbb). A line break is a '\n' in its words. Kept beside the plain words it spells (a label's
 * `text`, a test's or a shot's `note`, a callout's `custom`) and only when one of its stretches has a
 * look of its own: the plain words say everything else.
 */
export interface TextRun {
  t: string;
  b?: true;
  i?: true;
  u?: true;
  s?: true;
  c?: string;
}
export const TEXT_COLOR_RE = /^(#[0-9a-f]{6}|[a-z]{1,16})$/;
/**
 * A label's box as laid out by hand (2026-10-28, the inspectors: "shift text around as they see
 * fit"): its width (its words wrap within it; absent, as wide as its words), where its lines sit in
 * it (absent: from its left), and its type's size (× the map's; absent: the map's).
 */
export interface BoxLook {
  w?: number;
  align?: 'center' | 'right';
  size?: number;
}
/** The sizes a label's type may take, × the map's. */
export const LOOK_SIZES = [0.75, 1, 1.25, 1.5, 2] as const;

/** The plain words a label's runs spell. */
export function plainOf(runs: readonly TextRun[]): string {
  return runs.map((r) => r.t).join('');
}
/**
 * A document whose runs all spell their plain words: the runs of words written since without them
 * (by anything that writes only the plain words) are dropped — the words as written are what stands.
 */
export function withRunsInStep(doc: MapDoc): MapDoc {
  const stale = (rich: TextRun[] | undefined, text: string | null | undefined) => !!rich && plainOf(rich) !== (text ?? '');
  const label = (l: SharedLabel | undefined): SharedLabel | undefined => {
    if (!l || !stale(l.rich, l.text)) return l;
    const { rich: _r, ...rest } = l;
    return rest;
  };
  let soils = doc.soils;
  const area = label(soils.areaLabel)!;
  const fail = label(soils.failLabel);
  const markups = soils.markups?.map((m) => label(m)!);
  if (area !== soils.areaLabel || fail !== soils.failLabel || (markups && markups.some((m, i) => m !== soils.markups![i]))) soils = { ...soils, areaLabel: area, ...(fail ? { failLabel: fail } : {}), ...(markups ? { markups } : {}) };
  let changed = soils !== doc.soils;
  const items = doc.items.map((it) => {
    const text = it.t === 'test' ? it.note : it.t === 'elev' ? it.note : it.t === 'callout' ? it.custom : it.t === 'note' ? it.text : null;
    if (!('rich' in it) || !stale(it.rich, text)) return it;
    changed = true;
    const { rich: _r, ...rest } = it;
    return rest as Item;
  });
  return changed ? { ...doc, soils, items } : doc;
}
/** Two runs look alike. */
export function sameLook(a: TextRun, b: TextRun): boolean {
  return !!a.b === !!b.b && !!a.i === !!b.i && !!a.u === !!b.u && !!a.s === !!b.s && (a.c ?? '') === (b.c ?? '');
}
/**
 * A label's runs tidied: neighbours of one look joined, empty ones gone, the words trimmed (spaces
 * and line breaks at either end) and cut to `max` characters. Null when no stretch has a look of its
 * own: then the plain words are all there is to keep.
 */
export function tidyRuns(runs: readonly TextRun[], max: number): TextRun[] | null {
  const out: TextRun[] = [];
  for (const r of runs) {
    if (!r.t) continue;
    const last = out[out.length - 1];
    if (last && sameLook(last, r)) out[out.length - 1] = { ...last, t: last.t + r.t };
    else out.push({ ...r });
  }
  // trimmed at either end, as the plain words are
  while (out.length && !out[0]!.t.replace(/^\s+/, '')) out.shift();
  if (out.length) out[0] = { ...out[0]!, t: out[0]!.t.replace(/^\s+/, '') };
  while (out.length && !out[out.length - 1]!.t.replace(/\s+$/, '')) out.pop();
  if (out.length) out[out.length - 1] = { ...out[out.length - 1]!, t: out[out.length - 1]!.t.replace(/\s+$/, '') };
  // cut to the longest words may be
  let left = max;
  const cut: TextRun[] = [];
  for (const r of out) {
    if (left <= 0) break;
    cut.push(r.t.length > left ? { ...r, t: r.t.slice(0, left) } : r);
    left -= r.t.length;
  }
  // (at most as many runs as a document keeps: the words after, in the look of the last kept)
  if (cut.length > MAX_TEXT_RUNS) cut.splice(MAX_TEXT_RUNS - 1, cut.length, { ...cut[MAX_TEXT_RUNS - 1]!, t: cut.slice(MAX_TEXT_RUNS - 1).map((r) => r.t).join('') });
  return cut.some((r) => r.b || r.i || r.u || r.s || r.c) ? cut : null;
}
/** The most runs one label's words are kept in. */
export const MAX_TEXT_RUNS = 200;

export type Shape =
  | { k: 'rect'; x: number; y: number; w: number; h: number }
  | { k: 'poly'; pts: number[] } // flat [x0,y0,x1,y1,...], closed
  | PathShape
  | { k: 'stroke'; pts: number[]; width: number }; // flat polyline + band width (image px)

/**
 * A closed Bézier path: anchors + four handle numbers per anchor (in x,y, out x,y). `edit`: a
 * Magic Mark (an outline read off the plan) whose points are being edited through its `form` —
 * done, it lets the form go and is an outline again, with its end tabs.
 */
export interface PathShape {
  k: 'path';
  pts: number[];
  h: number[];
  form?: Form;
  edit?: true;
}

/**
 * A drawn shape as the person handles it (the path is made from it, and anything that reshapes
 * the path another way — a cut, a merge, a pulled end — lets it go):
 *  poly    – points the outline passes through, closed. At a corner (round 0) the outline turns
 *            sharply; through a round point (round 1) it flows on a circle through its neighbours,
 *            so two corners and a round point between them are an exact arc. `r`: a corner's
 *            rounding (image px) — the outline cuts the corner on the arc of that radius touching
 *            both its sides, as a rounded rectangle's corners do (absent: every corner sharp).
 *  rect    – a box: centre, width, height and rotation (radians); it resizes like a box. `r`: its
 *            corners' roundings, as a custom shape's (top left, top right, bottom right, bottom left).
 *  ellipse – centre, semi-axes and rotation (radians); a circle when rx = ry.
 */
export type Form =
  | { f: 'poly'; pts: number[]; round: number[]; r?: number[] }
  | { f: 'rect'; cx: number; cy: number; w: number; h: number; rot: number; r?: number[] }
  | { f: 'ellipse'; cx: number; cy: number; rx: number; ry: number; rot: number };

export type TestStatus = 'none' | 'fail' | 'pass';

/**
 * How a density test was taken: by nuclear gauge (the usual one) or by sand cone. Each method is
 * numbered on its own — sand cone 1, 2 … beside nuke 1, 2, 3 … on the same map and date.
 */
export type TestMethod = 'nuke' | 'sandcone';
export const TEST_METHODS: readonly TestMethod[] = ['nuke', 'sandcone'];

export interface WorkArea {
  t: 'area';
  id: string;
  shape: Shape;
  /** A failing area (red, with its own shared label); absent = a work area observed. */
  status?: 'fail';
  /**
   * The work area's colour: a name from the palette (`AREA_COLOR_KEYS`: green, yellow, blue …) or a
   * colour of the inspector's own as `#rrggbb`. Absent = the first of the palette (green). Its label,
   * the arrow onto it and a callout on it take the colour too. (A failing area is red whatever this says.)
   */
  color?: string;
  /**
   * Which markup the area belongs to, numbered from 1 in the order the inspector began them — a
   * soils map's "pours": each markup is one statement ("Over-excavation performed today", "Previous
   * over-excavation"), with its own colour and its own label pointing at every area of it. Areas of one
   * markup that overlap are one shape; areas of different markups never join and never overlap (a new
   * one yields to the ones already there). Absent means markup 1. (A failing area stands apart.)
   */
  markup?: number;
}

export interface TestPoint {
  t: 'test';
  id: string;
  p: Vec;
  n: number;
  date: string; // YYYYMMDD
  initials: string; // 1–4 uppercase letters
  status: TestStatus;
  retestOf: number | null;
  note: string;
  label: LabelPos;
  /** Its note with a look of its own (`TextRun`). */
  rich?: TextRun[];
  /** Its badge as laid out by hand: its width (the words after its name wrap), their alignment, its size. */
  look?: BoxLook;
  /** How it was taken; absent = by nuclear gauge. */
  method?: 'sandcone';
  /**
   * What the test is called on the map instead of its number — "94R", "A-3", whatever the inspector
   * typed (never a bare number: that is `n` itself). The number still counts: the next test follows
   * on from it, and a retest names it.
   */
  name?: string;
}
/** The most a test's own name may be. */
export const TEST_NAME_MAX = 24;

/**
 * An elevation shot: where an elevation was taken (a surveyor's rod or GPS rover on the bottom of an
 * over-excavation, a subgrade, a footing), numbered as it was taken, and the elevation read there —
 * kept exactly as it was typed ("2795.65"), never rounded. Its tag reads "#1 Elev. 2795.65".
 */
export interface Elevation {
  t: 'elev';
  id: string;
  p: Vec;
  n: number;
  /** The elevation as typed: digits, a point, a sign (empty until it is typed). */
  value: string;
  /** What the tag calls it before the value ("BOE", "FG", "" for nothing); absent: the map's (`ElevMeta.prefix`, "Elev."). */
  prefix?: string;
  /** A few words after the value ("bottom of removal"). */
  note?: string;
  /**
   * The grade it is measured against, as the inspector typed it ("FG", "2805.54"): the tag then says
   * how far below (or above) it the shot is — "9.89 ft below FG". Never read off the plan.
   */
  ref?: ElevRef;
  /** Where its tag was put by hand (top-left); null: placed automatically. */
  label: LabelPos;
  /** Its note with a look of its own (`TextRun`). */
  rich?: TextRun[];
  /** Its tag as laid out by hand: its width (its words wrap), their alignment, its size. */
  look?: BoxLook;
}
/** A grade a shot is measured against: its name ("FG", "EG", "BOF") and its elevation as typed. */
export interface ElevRef {
  name: string;
  value: string;
}
/** The most an elevation's value, its prefix and its note may be. */
export const ELEV_VALUE_MAX = 12;
export const ELEV_PREFIX_MAX = 12;
export const ELEV_NOTE_MAX = 60;
/** What a tag calls an elevation when nothing else is said. */
export const ELEV_PREFIX_DEFAULT = 'Elev.';
/**
 * The elevation tags' colours, as the elevation settings offer them: a bright blue first — the
 * default, as Ricky asked for it — then the others (`ELEV_COLORS` in src/render/style.ts). A tag may
 * also be a colour of the inspector's own, `#rrggbb`.
 */
export const ELEV_COLOR_KEYS = ['blue', 'red', 'ink', 'purple', 'orange', 'teal'] as const;
export type ElevColorKey = (typeof ELEV_COLOR_KEYS)[number];
/** An elevation tag's colour as stored: a palette name, or `#rrggbb` lower case. */
export const ELEV_COLOR_RE = /^(#[0-9a-f]{6}|[a-z]{1,16})$/;
/**
 * How a map's elevation tags read: what they call an elevation, their colour, where their numbering
 * starts, their unit (feet unless metres), and — said once, in the legend — what was shot and how.
 */
export interface ElevMeta {
  prefix?: string;
  /** A palette name (`ELEV_COLOR_KEYS`) or `#rrggbb`; absent: the bright blue. */
  color?: string;
  start?: number;
  unit?: 'm';
  /** What every shot on the map is of ("bottom of over-excavation"). */
  what?: string;
  /** How they were taken ("GPS rover, contractor's grade checker"). */
  source?: string;
}
export const ELEV_META_TEXT_MAX = 60;
/**
 * An elevation as typed, made plain: a sign, digits and one point, nothing else (a thousands comma,
 * "2,795.65", is dropped — never read as a point). Never rounded: the digits are the inspector's.
 */
export function cleanElevation(raw: string): string {
  let s = raw.replace(/[^0-9.\-]/g, '');
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  const dot = s.indexOf('.');
  if (dot >= 0) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  return ((neg ? '-' : '') + s).slice(0, ELEV_VALUE_MAX);
}

export interface Highlight {
  t: 'hl';
  id: string;
  material: string; // material key, see MATERIALS
  shape: Shape;
  /**
   * Which placement (pour) the mark belongs to, numbered from 1 in the order the
   * inspector marked them. Marks of one placement and material that overlap are
   * one shape; marks of different placements never join and never overlap (a
   * new one yields to the ones already there). Absent means placement 1.
   */
  placement?: number;
}

export type CalloutTemplate = 'between' | 'at' | 'custom';

/** One arrow of a callout: the mark it points at, and where on it. */
export interface CalloutTarget {
  target: Vec; // leader tip (fallback when targetId is missing)
  targetId: string | null; // highlight id the leader attaches to
  /** The arrow's tip stays at `target`, where the person put it, instead of being chosen on the mark. */
  pinned?: boolean;
  /** A tip put on one of the mark's ends (the head points along the mark). Absent = on the side facing the box. */
  face?: 'left' | 'right';
  /**
   * Taken off (2026-10-24, Adam: an arrow shaken off its mark): not drawn. Only ever a callout's
   * last arrow — one of several is taken away altogether — so the callout keeps its mark.
   */
  off?: true;
}

/** One more stretch of a callout: "and between SC1 stations 175+73 and 175+90". */
export interface CalloutRange {
  alignment: string;
  from: string;
  to: string;
}

export interface Callout extends CalloutTarget {
  t: 'callout';
  id: string;
  material: string | null;
  template: CalloutTemplate;
  alignment: string; // e.g. "SC1", "PDM/TEL", "Mainline"
  from: string; // station text e.g. "99+30"
  to: string; // station text e.g. "100+41"
  custom: string; // used when template === 'custom'
  /** Further stretches after the first (one statement for several marks along a line), each a line of its own. */
  ranges?: CalloutRange[];
  box: LabelPos; // top-left of the callout box; null = auto
  /**
   * The other marks the callout points at, each with an arrow of its own (one statement for
   * several marks: "SC1/SC2 stations …"). The first arrow is the callout's own `target`.
   */
  also?: CalloutTarget[];
  /** The material word as typed (replaces the material's name; empty = no word). Absent = the material's name. */
  materialName?: string;
  /**
   * Its alignment and stations were read off the plan (the stations the marks it points at span),
   * not typed: they follow the marks as they are reshaped, until they are typed over.
   */
  auto?: boolean;
  /** Its words of its own (`custom`) with a look of their own (`TextRun`). */
  rich?: TextRun[];
  /** Its box as laid out by hand: its width (its lines wrap), their alignment, its size. */
  look?: BoxLook;
}

export interface Note {
  t: 'note';
  id: string;
  p: Vec; // label position (top-left)
  target: Vec | null; // optional leader tip
  text: string;
  color: string; // material/color key
  /** Its words with a look of their own (`TextRun`). */
  rich?: TextRun[];
  /** Its box as laid out by hand. */
  look?: BoxLook;
}

/**
 * A shape cut out of a mark (`of`): the mark is drawn, hit and read as itself less every shape cut
 * out of it, the ones that overlap together as one. Each stays a shape of its own — a square, a
 * circle, a drawn shape — handled and edited as any while the mark is being cut into.
 */
export interface Cut {
  t: 'cut';
  id: string;
  of: string;
  shape: Shape;
}

export type Item = WorkArea | TestPoint | Elevation | Highlight | Callout | Note | Cut;
export type ItemType = Item['t'];

/** The named work area colours, in the order of the strip; the first is the default. (Their values: src/render/style.ts.) */
export const AREA_COLOR_KEYS = ['green', 'yellow', 'blue', 'orange', 'purple', 'pink', 'teal', 'lime', 'brown', 'slate', 'indigo'] as const;
export type AreaColorKey = (typeof AREA_COLOR_KEYS)[number];
/** A work area's colour as stored: a palette name, or `#rrggbb` lower case. */
export const AREA_COLOR_RE = /^(#[0-9a-f]{6}|[a-z]{1,16})$/;

/** Where a shared label's arrow onto an area was put by hand (the arrow's tip, and the end of the area it is on, if an end). */
export interface ArrowPin {
  target: Vec;
  face?: 'left' | 'right';
  /** The arrow onto this area taken off (shaken off it): the label labels the others; Arrows to the middle brings it back. */
  off?: true;
}
/** One of a soils map's shared labels: its words (null = automatic), its place (null = automatic), and its arrows put by hand (by area id). */
export interface SharedLabel {
  text: string | null;
  pos: LabelPos;
  pins?: Record<string, ArrowPin>;
  /** Deleted from the map (2026-10-23, Adam: "why isn't it possible to delete this?"): no label, no arrows; its areas stay. Its words are kept, and editing them shows it again. */
  hidden?: true;
  /** Its words with a look of their own (`TextRun`). */
  rich?: TextRun[];
  /** Its box as laid out by hand: its width (its words wrap), their alignment, its size. */
  look?: BoxLook;
}

/**
 * What a soils map's test badges say — the number is the test's own (or the name it was given):
 * test "Test #6" (the default), hash "#6", num "6", dash "T-6", plain "Test 6", id "20260922_AB_6"
 * (date, initials, number: the badge says "#6" and the legend what it stands for). The legend, the
 * bar and a retest name tests the same way.
 */
export type TestStyle = 'test' | 'hash' | 'num' | 'dash' | 'plain' | 'id';
export const TEST_STYLES: readonly TestStyle[] = ['test', 'hash', 'num', 'dash', 'plain', 'id'];

export interface SoilsMeta {
  date: string; // default YYYYMMDD for new tests
  initials: string; // default initials for new tests
  /** How the map's test labels read (absent = "Test #6"). */
  testStyle?: TestStyle;
  areaLabel: SharedLabel; // null text = automatic
  /** The failing areas' shared label (absent = automatic wording and place). */
  failLabel?: SharedLabel;
  /**
   * The labels of markups 2, 3 … (markup 1's is `areaLabel`): `markups[n - 2]` is markup n's. Each
   * points at every work area of its markup; absent or null text: "Work area(s) observed".
   */
  markups?: SharedLabel[];
  /**
   * The number each method's tests start from on this map (set as its first test is placed, or
   * chosen in Test labels): the next test takes the lowest number from there not in use, so a test
   * deleted frees its number. Absent (older maps): from the lowest number in use.
   */
  start?: Partial<Record<TestMethod, number>>;
}

export interface MapDoc {
  v: typeof DOC_VERSION;
  kind: MapKind;
  w: number;
  h: number;
  scale: number; // markup size multiplier, 1 = default
  legend: boolean; // concrete: draw a material legend
  soils: SoilsMeta;
  /** How the elevation tags read (absent: "Elev.", blue, numbered from 1). */
  elev?: ElevMeta;
  /**
   * The pinpoints — a test's dot, a shot's ring — left off the map (2026-10-23, Adam: "an option to
   * remove all the pinpoint dots"): each leader then simply ends on its spot. Absent: shown.
   */
  noDots?: true;
  items: Item[];
}

/* ------------------------------------------------------------------------ */
/* Materials (concrete mode)                                                */
/* ------------------------------------------------------------------------ */

export interface Material {
  key: string;
  name: string; // short label used in callouts, e.g. "FTB"
  long: string; // legend description
  color: string; // hex
  /** A colour more than a material: chosen from the colours (the strip offers the one last chosen), its word typed in a callout. */
  swatchOnly?: boolean;
}

/** In the order of the material strip; the first is the default for a new map. */
export const MATERIALS: readonly Material[] = [
  { key: 'tc', name: 'TC', long: 'Thermal concrete', color: '#E0453D' },
  { key: 'ftb', name: 'FTB', long: 'Fluidized thermal backfill', color: '#2F6FE4' },
  { key: 'sand', name: 'Sand', long: 'Sand bedding / backfill', color: '#B58900' },
  { key: 'conc', name: 'Concrete', long: 'Structural concrete', color: '#1FA362', swatchOnly: true },
  { key: 'clsm', name: 'CLSM', long: 'Controlled low-strength material (slurry)', color: '#8A4DD6', swatchOnly: true },
  { key: 'pink', name: 'Pink', long: 'Pink (as noted)', color: '#E255B1', swatchOnly: true },
  { key: 'orange', name: 'Orange', long: 'Orange (as noted)', color: '#F07F1F', swatchOnly: true },
  { key: 'yellow', name: 'Yellow', long: 'Yellow (as noted)', color: '#E3BC00', swatchOnly: true },
  { key: 'lime', name: 'Lime', long: 'Lime (as noted)', color: '#76B51B', swatchOnly: true },
  { key: 'teal', name: 'Teal', long: 'Teal (as noted)', color: '#0F9E9A', swatchOnly: true },
  { key: 'sky', name: 'Sky', long: 'Sky blue (as noted)', color: '#27A4E6', swatchOnly: true },
  { key: 'indigo', name: 'Indigo', long: 'Indigo (as noted)', color: '#4B55D6', swatchOnly: true },
  { key: 'magenta', name: 'Magenta', long: 'Magenta (as noted)', color: '#C2189B', swatchOnly: true },
  { key: 'brown', name: 'Brown', long: 'Brown (as noted)', color: '#8E5B2E', swatchOnly: true },
  { key: 'gray', name: 'Gray', long: 'Gray (as noted)', color: '#6E7781', swatchOnly: true },
  { key: 'black', name: 'Black', long: 'Black (as noted)', color: '#26282B', swatchOnly: true },
];

export function materialByKey(key: string | null | undefined): Material {
  return MATERIALS.find((m) => m.key === key) ?? MATERIALS[0]!;
}

/* ------------------------------------------------------------------------ */
/* Construction                                                             */
/* ------------------------------------------------------------------------ */

export function newDoc(kind: MapKind, w: number, h: number, soils: { date: string; initials: string }): MapDoc {
  return {
    v: DOC_VERSION,
    kind,
    w,
    h,
    scale: 1,
    legend: false,
    soils: { date: soils.date, initials: soils.initials, areaLabel: { text: null, pos: null } },
    items: [],
  };
}

/* ------------------------------------------------------------------------ */
/* Validation (strict; used by the Worker and when loading local storage)   */
/* ------------------------------------------------------------------------ */

export class DocError extends Error {
  override name = 'DocError';
}

const MAX_ITEMS = 2000;
const MAX_PTS = 20_000;
const MAX_TEXT = 500;
const MAX_ARROWS = 24;
const MAX_RANGES = 8;
const MAX_PINS = 256;
const MAX_MARKUPS = 999;
const MAX_DIM = 20_000;

function fail(msg: string): never {
  throw new DocError(msg);
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function num(v: unknown, what: string, min = -1e7, max = 1e7): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail(`${what}: not a finite number`);
  if (v < min || v > max) fail(`${what}: out of range`);
  return v;
}
function int(v: unknown, what: string, min: number, max: number): number {
  const n = num(v, what, min, max);
  if (!Number.isInteger(n)) fail(`${what}: not an integer`);
  return n;
}
function str(v: unknown, what: string, max = MAX_TEXT): string {
  if (typeof v !== 'string') fail(`${what}: not a string`);
  if (v.length > max) fail(`${what}: too long`);
  return v;
}
function id(v: unknown, what: string): string {
  const s = str(v, what, 64);
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(s)) fail(`${what}: bad id`);
  return s;
}
function vec(v: unknown, what: string): Vec {
  if (!isObj(v)) fail(`${what}: not a point`);
  return { x: num(v.x, `${what}.x`), y: num(v.y, `${what}.y`) };
}
function labelPos(v: unknown, what: string): LabelPos {
  return v === null || v === undefined ? null : vec(v, what);
}
function pts(v: unknown, what: string): number[] {
  if (!Array.isArray(v)) fail(`${what}: not an array`);
  if (v.length % 2 !== 0 || v.length > MAX_PTS) fail(`${what}: bad length`);
  return v.map((n, i) => num(n, `${what}[${i}]`));
}
function shape(v: unknown, what: string): Shape {
  if (!isObj(v)) fail(`${what}: not a shape`);
  switch (v.k) {
    case 'rect':
      return { k: 'rect', x: num(v.x, `${what}.x`), y: num(v.y, `${what}.y`), w: num(v.w, `${what}.w`), h: num(v.h, `${what}.h`) };
    case 'poly':
      return { k: 'poly', pts: pts(v.pts, `${what}.pts`) };
    case 'path': {
      const a = pts(v.pts, `${what}.pts`);
      const h = pts(v.h, `${what}.h`);
      if (h.length !== 2 * a.length) fail(`${what}.h: bad length`);
      const out: PathShape = { k: 'path', pts: a, h };
      if (v.form !== undefined && v.form !== null) out.form = form(v.form, `${what}.form`);
      if (v.edit === true && out.form) out.edit = true;
      return out;
    }
    case 'stroke':
      return { k: 'stroke', pts: pts(v.pts, `${what}.pts`), width: num(v.width, `${what}.width`, 0.5, MAX_DIM) };
    default:
      return fail(`${what}: unknown shape kind`);
  }
}
const MAX_HOLES = 64;
/** Holes kept on a path shape (2026-09-25, for a day): each read as a cut of the mark. */
function holesOf(v: unknown, what: string): { pts: number[]; h: number[] }[] {
  if (!isObj(v) || v.holes === undefined || v.holes === null) return [];
  if (!Array.isArray(v.holes) || v.holes.length > MAX_HOLES) fail(`${what}.holes: bad`);
  return v.holes.map((x, i) => {
    if (!isObj(x)) fail(`${what}.holes[${i}]: not a hole`);
    const a = pts(x.pts, `${what}.holes[${i}].pts`);
    const h = pts(x.h, `${what}.holes[${i}].h`);
    if (a.length < 6 || h.length !== 2 * a.length) fail(`${what}.holes[${i}]: bad length`);
    return { pts: a, h };
  });
}
const MAX_FORM_PTS = 200;
function form(v: unknown, what: string): Form {
  if (!isObj(v)) fail(`${what}: not a form`);
  switch (v.f) {
    case 'poly': {
      const p = pts(v.pts, `${what}.pts`);
      if (p.length < 6 || p.length > 2 * MAX_FORM_PTS) fail(`${what}.pts: bad length`);
      if (!Array.isArray(v.round) || v.round.length !== p.length / 2) fail(`${what}.round: bad length`);
      const round = v.round.map((r, i) => (r === 0 || r === 1 ? r : fail(`${what}.round[${i}]: not 0 or 1`)));
      const out: Form = { f: 'poly', pts: p, round };
      if (v.r !== undefined && v.r !== null) {
        if (!Array.isArray(v.r) || v.r.length !== round.length) fail(`${what}.r: bad length`);
        const r = v.r.map((x, i) => num(x, `${what}.r[${i}]`, 0, MAX_DIM));
        // a rounding only a corner has, and kept only when some corner is rounded
        if (r.some((x, i) => x > 0 && !round[i])) out.r = r.map((x, i) => (round[i] ? 0 : x));
      }
      return out;
    }
    case 'rect': {
      const out: Form = { f: 'rect', cx: num(v.cx, `${what}.cx`), cy: num(v.cy, `${what}.cy`), w: num(v.w, `${what}.w`, 0.1, 2 * MAX_DIM), h: num(v.h, `${what}.h`, 0.1, 2 * MAX_DIM), rot: num(v.rot, `${what}.rot`, -100, 100) };
      if (v.r !== undefined && v.r !== null) {
        if (!Array.isArray(v.r) || v.r.length !== 4) fail(`${what}.r: bad length`);
        const r = v.r.map((x, i) => num(x, `${what}.r[${i}]`, 0, MAX_DIM));
        if (r.some((x) => x > 0)) out.r = r;
      }
      return out;
    }
    case 'ellipse':
      return { f: 'ellipse', cx: num(v.cx, `${what}.cx`), cy: num(v.cy, `${what}.cy`), rx: num(v.rx, `${what}.rx`, 0.1, MAX_DIM), ry: num(v.ry, `${what}.ry`, 0.1, MAX_DIM), rot: num(v.rot, `${what}.rot`, -100, 100) };
    default:
      return fail(`${what}: unknown form`);
  }
}
export const DATE_RE = /^\d{8}$/;
export const INITIALS_RE = /^[A-Z]{1,4}$/;
function date(v: unknown, what: string): string {
  const s = str(v, what, 32);
  if (!DATE_RE.test(s)) fail(`${what}: expected YYYYMMDD`);
  return s;
}
function initials(v: unknown, what: string): string {
  const s = str(v, what, 4);
  if (!INITIALS_RE.test(s)) fail(`${what}: expected 1–4 uppercase letters`);
  return s;
}
function materialKey(v: unknown, what: string): string {
  const s = str(v, what, 32);
  if (!/^[a-z0-9_-]{1,32}$/.test(s)) fail(`${what}: bad material key`);
  return s;
}
function areaColor(v: unknown, what: string): string {
  const s = str(v, what, 16).toLowerCase();
  if (!AREA_COLOR_RE.test(s)) fail(`${what}: bad colour`);
  return s;
}
/** A shared label's arrows put by hand, by the id of the area each is on. */
function pins(v: unknown, what: string): Record<string, ArrowPin> | undefined {
  if (v === undefined || v === null) return undefined;
  if (!isObj(v)) fail(`${what}: bad`);
  const keys = Object.keys(v);
  if (keys.length > MAX_PINS) fail(`${what}: too many`);
  const out: Record<string, ArrowPin> = {};
  for (const k of keys) {
    const p = v[k];
    if (!isObj(p)) fail(`${what}.${k}: not a pin`);
    const pin: ArrowPin = { target: vec(p.target, `${what}.${k}.target`) };
    if (p.face === 'left' || p.face === 'right') pin.face = p.face;
    if (p.off === true) pin.off = true;
    out[id(k, `${what} key`)] = pin;
  }
  return keys.length ? out : undefined;
}
const MAX_RUNS = MAX_TEXT_RUNS;
/** A label's runs (`TextRun`), tidied — or none when there are none, or none with a look of its own. */
function runs(v: unknown, what: string, max: number): TextRun[] | null {
  if (v === undefined || v === null) return null;
  if (!Array.isArray(v) || v.length > MAX_RUNS) fail(`${what}: bad`);
  const list = v.map((r, i): TextRun => {
    if (!isObj(r)) fail(`${what}[${i}]: not a run`);
    const run: TextRun = { t: str(r.t ?? '', `${what}[${i}].t`, MAX_TEXT) };
    if (r.b === true) run.b = true;
    if (r.i === true) run.i = true;
    if (r.u === true) run.u = true;
    if (r.s === true) run.s = true;
    if (r.c !== undefined && r.c !== null) {
      if (typeof r.c !== 'string' || !TEXT_COLOR_RE.test(r.c)) fail(`${what}[${i}].c: bad`);
      run.c = r.c;
    }
    return run;
  });
  return tidyRuns(list, max);
}
/** A label's box as laid out by hand, or none. */
function look(v: unknown, what: string): BoxLook | undefined {
  if (v === undefined || v === null) return undefined;
  if (!isObj(v)) fail(`${what}: bad`);
  const out: BoxLook = {};
  if (v.w !== undefined && v.w !== null) out.w = num(v.w, `${what}.w`, 1, MAX_DIM);
  if (v.align === 'center' || v.align === 'right') out.align = v.align;
  else if (v.align !== undefined && v.align !== null && v.align !== 'left') fail(`${what}.align: bad`);
  if (v.size !== undefined && v.size !== null) {
    const k = num(v.size, `${what}.size`, 0.5, 3);
    if (Math.abs(k - 1) > 1e-6) out.size = k;
  }
  return Object.keys(out).length ? out : undefined;
}
/** Runs and a look onto an item that has them: the plain words (`key`) then say what the runs spell. */
function withText<T extends { rich?: TextRun[]; look?: BoxLook }>(o: T, v: Record<string, unknown>, what: string, key: keyof T & string, max: number): T {
  const r = runs(v.rich, `${what}.rich`, max);
  if (r) {
    o.rich = r;
    (o as Record<string, unknown>)[key] = plainOf(r);
  }
  const l = look(v.look, `${what}.look`);
  if (l) o.look = l;
  return o;
}

function sharedLabel(v: unknown, what: string, maxText: number): SharedLabel {
  const o = isObj(v) ? v : {};
  const out: SharedLabel = { text: o.text === null || o.text === undefined ? null : str(o.text, `${what}.text`, maxText), pos: labelPos(o.pos, `${what}.pos`) };
  const p = pins(o.pins, `${what}.pins`);
  if (p) out.pins = p;
  if (o.hidden === true) out.hidden = true;
  return withText(out, o, what, 'text', maxText);
}

function calloutTarget(v: Record<string, unknown>, what: string): CalloutTarget {
  const t: CalloutTarget = {
    target: vec(v.target, `${what}.target`),
    targetId: v.targetId === null || v.targetId === undefined ? null : id(v.targetId, `${what}.targetId`),
  };
  if (v.pinned === true) t.pinned = true;
  if (v.face === 'left' || v.face === 'right') t.face = v.face;
  if (v.off === true) t.off = true;
  return t;
}

function item(v: unknown, what: string): Item {
  if (!isObj(v)) fail(`${what}: not an item`);
  const t = v.t;
  const iid = id(v.id, `${what}.id`);
  switch (t) {
    case 'area': {
      const a: WorkArea = { t, id: iid, shape: shape(v.shape, `${what}.shape`) };
      if (v.status === 'fail') a.status = 'fail';
      else if (v.status !== undefined && v.status !== null) fail(`${what}.status: bad`);
      if (v.color !== undefined && v.color !== null) {
        const c = areaColor(v.color, `${what}.color`);
        if (c !== AREA_COLOR_KEYS[0]) a.color = c;
      }
      if (v.markup !== undefined && v.markup !== null) {
        const n = int(v.markup, `${what}.markup`, 1, 9999);
        if (n !== 1) a.markup = n;
      }
      return a;
    }
    case 'test': {
      const status = v.status;
      if (status !== 'none' && status !== 'fail' && status !== 'pass') fail(`${what}.status: bad`);
      const tp: TestPoint = {
        t,
        id: iid,
        p: vec(v.p, `${what}.p`),
        n: int(v.n, `${what}.n`, 1, 9999),
        date: date(v.date, `${what}.date`),
        initials: initials(v.initials, `${what}.initials`),
        status,
        retestOf: v.retestOf === null || v.retestOf === undefined ? null : int(v.retestOf, `${what}.retestOf`, 1, 9999),
        note: str(v.note ?? '', `${what}.note`, 120),
        label: labelPos(v.label, `${what}.label`),
      };
      if (v.method === 'sandcone') tp.method = 'sandcone';
      else if (v.method !== undefined && v.method !== null && v.method !== 'nuke') fail(`${what}.method: bad`);
      if (v.name !== undefined && v.name !== null) {
        const name = str(v.name, `${what}.name`, TEST_NAME_MAX).trim();
        if (name && !/^\d+$/.test(name)) tp.name = name;
      }
      return withText(tp, v, what, 'note', 120);
    }
    case 'elev': {
      const e: Elevation = {
        t,
        id: iid,
        p: vec(v.p, `${what}.p`),
        n: int(v.n, `${what}.n`, 1, 9999),
        value: cleanElevation(str(v.value ?? '', `${what}.value`, 40)),
        label: labelPos(v.label, `${what}.label`),
      };
      if (v.prefix !== undefined && v.prefix !== null) e.prefix = str(v.prefix, `${what}.prefix`, 40).trim().slice(0, ELEV_PREFIX_MAX);
      if (v.note !== undefined && v.note !== null) {
        const note = str(v.note, `${what}.note`, 200).trim().slice(0, ELEV_NOTE_MAX);
        if (note) e.note = note;
      }
      if (isObj(v.ref)) {
        const name = str(v.ref.name ?? '', `${what}.ref.name`, 40).trim().slice(0, ELEV_PREFIX_MAX);
        const value = cleanElevation(str(v.ref.value ?? '', `${what}.ref.value`, 40));
        if (value) e.ref = { name, value };
      }
      withText(e, v, what, 'note', ELEV_NOTE_MAX);
      if (e.note === '') delete e.note;
      return e;
    }
    case 'hl': {
      const hl: Highlight = { t, id: iid, material: materialKey(v.material, `${what}.material`), shape: shape(v.shape, `${what}.shape`) };
      if (v.placement !== undefined && v.placement !== null) hl.placement = int(v.placement, `${what}.placement`, 1, 9999);
      return hl;
    }
    case 'callout': {
      const template = v.template;
      if (template !== 'between' && template !== 'at' && template !== 'custom') fail(`${what}.template: bad`);
      const c: Callout = {
        t,
        id: iid,
        material: v.material === null || v.material === undefined ? null : materialKey(v.material, `${what}.material`),
        template,
        alignment: str(v.alignment ?? '', `${what}.alignment`, 60),
        from: str(v.from ?? '', `${what}.from`, 20),
        to: str(v.to ?? '', `${what}.to`, 20),
        custom: str(v.custom ?? '', `${what}.custom`, 240),
        box: labelPos(v.box, `${what}.box`),
        ...calloutTarget(v, what),
      };
      if (v.also !== undefined && v.also !== null) {
        if (!Array.isArray(v.also) || v.also.length > MAX_ARROWS) fail(`${what}.also: bad`);
        const also = v.also.map((a, i) => (isObj(a) ? calloutTarget(a, `${what}.also[${i}]`) : fail(`${what}.also[${i}]: not an arrow`)));
        if (also.length) c.also = also;
      }
      if (v.ranges !== undefined && v.ranges !== null) {
        if (!Array.isArray(v.ranges) || v.ranges.length > MAX_RANGES) fail(`${what}.ranges: bad`);
        const ranges = v.ranges.map((r, i): CalloutRange => {
          if (!isObj(r)) fail(`${what}.ranges[${i}]: not a range`);
          return { alignment: str(r.alignment ?? '', `${what}.ranges[${i}].alignment`, 60), from: str(r.from ?? '', `${what}.ranges[${i}].from`, 20), to: str(r.to ?? '', `${what}.ranges[${i}].to`, 20) };
        });
        if (ranges.length) c.ranges = ranges;
      }
      if (typeof v.materialName === 'string') c.materialName = str(v.materialName, `${what}.materialName`, 40);
      if (v.auto === true) c.auto = true;
      return withText(c, v, what, 'custom', 240);
    }
    case 'cut': {
      const c: Cut = { t, id: iid, of: id(v.of, `${what}.of`), shape: shape(v.shape, `${what}.shape`) };
      if (c.shape.k === 'stroke') fail(`${what}.shape: a cut is closed`);
      return c;
    }
    case 'note':
      return withText<Note>(
        {
          t,
          id: iid,
          p: vec(v.p, `${what}.p`),
          target: v.target === null || v.target === undefined ? null : vec(v.target, `${what}.target`),
          text: str(v.text ?? '', `${what}.text`, 240),
          color: materialKey(v.color ?? 'red', `${what}.color`),
        },
        v,
        what,
        'text',
        240,
      );
    default:
      return fail(`${what}: unknown item type`);
  }
}

/** Parse and validate an untrusted value into a MapDoc. Throws DocError. */
export function parseDoc(v: unknown): MapDoc {
  if (!isObj(v)) fail('doc: not an object');
  if (v.v !== DOC_VERSION) fail('doc: unsupported version');
  const kind = v.kind;
  if (kind !== 'soils' && kind !== 'concrete') fail('doc.kind: bad');
  const w = int(v.w, 'doc.w', 1, MAX_DIM);
  const h = int(v.h, 'doc.h', 1, MAX_DIM);
  const scale = num(v.scale ?? 1, 'doc.scale', 0.25, 4);
  const legend = typeof v.legend === 'boolean' ? v.legend : false;
  if (!isObj(v.soils)) fail('doc.soils: bad');
  const soils: SoilsMeta = {
    date: date(v.soils.date, 'doc.soils.date'),
    initials: initials(v.soils.initials, 'doc.soils.initials'),
    areaLabel: sharedLabel(v.soils.areaLabel, 'doc.soils.areaLabel', 80),
  };
  if (v.soils.testStyle !== undefined && v.soils.testStyle !== null) {
    const ts = v.soils.testStyle;
    if (typeof ts !== 'string' || !(TEST_STYLES as readonly string[]).includes(ts)) fail('doc.soils.testStyle: bad');
    soils.testStyle = ts as TestStyle;
  }
  if (isObj(v.soils.start)) {
    const st = v.soils.start;
    const start: Partial<Record<TestMethod, number>> = {};
    for (const m of TEST_METHODS) if (st[m] !== undefined && st[m] !== null) start[m] = int(st[m], `doc.soils.start.${m}`, 1, 9999);
    if (Object.keys(start).length) soils.start = start;
  }
  if (isObj(v.soils.failLabel)) soils.failLabel = sharedLabel(v.soils.failLabel, 'doc.soils.failLabel', 80);
  if (Array.isArray(v.soils.markups)) {
    if (v.soils.markups.length > MAX_MARKUPS) fail('doc.soils.markups: too many');
    const markups = v.soils.markups.map((l, i) => sharedLabel(l, `doc.soils.markups[${i}]`, 80));
    if (markups.length) soils.markups = markups;
  }
  let elev: ElevMeta | undefined;
  if (isObj(v.elev)) {
    const e: ElevMeta = {};
    if (v.elev.prefix !== undefined && v.elev.prefix !== null) e.prefix = str(v.elev.prefix, 'doc.elev.prefix', 40).trim().slice(0, ELEV_PREFIX_MAX);
    if (typeof v.elev.color === 'string') {
      // a colour of the palette or of the inspector's own; the default (blue) is not written; anything else is let go
      const c = v.elev.color.trim().toLowerCase();
      if (ELEV_COLOR_RE.test(c) && (c.startsWith('#') || (ELEV_COLOR_KEYS as readonly string[]).includes(c)) && c !== ELEV_COLOR_KEYS[0]) e.color = c;
    }
    if (v.elev.start !== undefined && v.elev.start !== null) e.start = int(v.elev.start, 'doc.elev.start', 1, 9999);
    if (v.elev.unit === 'm') e.unit = 'm';
    for (const k of ['what', 'source'] as const) {
      if (v.elev[k] === undefined || v.elev[k] === null) continue;
      const t = str(v.elev[k], `doc.elev.${k}`, 200).trim().slice(0, ELEV_META_TEXT_MAX);
      if (t) e[k] = t;
    }
    if (Object.keys(e).length) elev = e;
  }
  if (!Array.isArray(v.items)) fail('doc.items: not an array');
  if (v.items.length > MAX_ITEMS) fail('doc.items: too many');
  const seen = new Set<string>();
  const items: Item[] = [];
  v.items.forEach((it, i) => {
    const parsed = item(it, `doc.items[${i}]`);
    if (seen.has(parsed.id)) fail(`doc.items[${i}]: duplicate id`);
    seen.add(parsed.id);
    items.push(parsed);
    // holes kept on a mark's shape (for a day) are cuts of the mark
    if ((parsed.t === 'area' || parsed.t === 'hl') && parsed.shape.k === 'path') {
      holesOf(isObj(it) ? it.shape : null, `doc.items[${i}].shape`).forEach((hl, k) => {
        const cid = `${parsed.id.slice(0, 56)}-h${k}`;
        if (seen.has(cid)) return;
        seen.add(cid);
        items.push({ t: 'cut', id: cid, of: parsed.id, shape: { k: 'path', pts: hl.pts, h: hl.h } });
      });
    }
  });
  if (items.length > MAX_ITEMS) fail('doc.items: too many');
  return { v: DOC_VERSION, kind, w, h, scale, legend, soils, ...(elev ? { elev } : {}), ...(v.noDots === true ? { noDots: true as const } : {}), items };
}

/** Cheap structural check used before accepting a JSON string on the server. */
export function parseDocJson(json: string, maxBytes = 2_000_000): MapDoc {
  if (json.length > maxBytes) fail('doc: too large');
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    fail('doc: invalid JSON');
  }
  return parseDoc(raw);
}

/* ------------------------------------------------------------------------ */
/* Placements (concrete mode)                                               */
/* ------------------------------------------------------------------------ */

/** The placement a mark belongs to (marks from before placements existed are placement 1). */
export function placementOf(hl: Pick<Highlight, 'placement'>): number {
  return hl.placement ?? 1;
}

/** Every placement number in use, ascending. */
export function placementsIn(items: readonly Item[]): number[] {
  const set = new Set<number>();
  for (const it of items) if (it.t === 'hl') set.add(placementOf(it));
  return [...set].sort((a, b) => a - b);
}

/**
 * Placement numbers made dense — 1 … k in the order the placements were made — so
 * after a deletion the numbers say exactly how many placements the map holds.
 * Returns the doc itself when nothing changes, and the old → new mapping.
 */
export function normalisePlacements(doc: MapDoc): { doc: MapDoc; map: Map<number, number> } {
  const map = new Map<number, number>();
  placementsIn(doc.items).forEach((n, i) => map.set(n, i + 1));
  let changed = false;
  for (const [from, to] of map) if (from !== to) changed = true;
  if (!changed) return { doc, map };
  const items = doc.items.map((it) => {
    if (it.t !== 'hl') return it;
    const to = map.get(placementOf(it))!;
    return to === placementOf(it) ? it : { ...it, placement: to };
  });
  return { doc: { ...doc, items }, map };
}

/* ------------------------------------------------------------------------ */
/* Markups (soils mode)                                                     */
/* ------------------------------------------------------------------------ */

/** The markup a work area belongs to (areas from before markups existed are markup 1). */
export function markupOf(a: Pick<WorkArea, 'markup'>): number {
  return a.markup ?? 1;
}

/** Every markup number in use by the work areas (failing areas stand apart), ascending. */
export function markupsIn(items: readonly Item[]): number[] {
  const set = new Set<number>();
  for (const it of items) if (it.t === 'area' && it.status !== 'fail') set.add(markupOf(it));
  return [...set].sort((a, b) => a - b);
}

/** A markup's label as the document keeps it (markup 1's is `areaLabel`; one never set is automatic). */
export function markupLabel(soils: SoilsMeta, n: number): SharedLabel {
  if (n <= 1) return soils.areaLabel;
  return soils.markups?.[n - 2] ?? { text: null, pos: null };
}

/** The soils meta with a markup's label set (the ones between it and the last kept, automatic where never set). */
export function withMarkupLabel(soils: SoilsMeta, n: number, label: SharedLabel): SoilsMeta {
  if (n <= 1) return { ...soils, areaLabel: label };
  const list = (soils.markups ?? []).slice();
  while (list.length < n - 2) list.push({ text: null, pos: null });
  list[n - 2] = label;
  return { ...soils, markups: list };
}

/**
 * Markup numbers made dense — 1 … k in the order the markups were begun — so after a markup's last
 * area goes the numbers still say how many there are; each label goes with its markup, and a failing
 * area keeps no markup of its own. Returns the doc itself when nothing changes, and the old → new mapping.
 */
export function normaliseMarkups(doc: MapDoc): { doc: MapDoc; map: Map<number, number> } {
  const map = new Map<number, number>();
  markupsIn(doc.items).forEach((n, i) => map.set(n, i + 1));
  let changed = doc.items.some((it) => it.t === 'area' && it.status === 'fail' && it.markup !== undefined);
  for (const [from, to] of map) if (from !== to) changed = true;
  // labels kept for markups no longer on the map go
  const kept = Math.max(0, map.size - 1);
  if ((doc.soils.markups?.length ?? 0) > kept) changed = true;
  if (!changed) return { doc, map };
  const items = doc.items.map((it) => {
    if (it.t !== 'area') return it;
    if (it.status === 'fail') {
      if (it.markup === undefined) return it;
      const { markup: _m, ...rest } = it;
      return rest;
    }
    const to = map.get(markupOf(it))!;
    if (to === markupOf(it)) return it;
    const { markup: _m, ...rest } = it;
    return to === 1 ? rest : { ...rest, markup: to };
  });
  let soils: SoilsMeta = { ...doc.soils };
  delete soils.markups;
  const labels = [...map].map(([from]) => markupLabel(doc.soils, from));
  soils = { ...soils, areaLabel: labels[0] ?? doc.soils.areaLabel };
  if (labels.length > 1) soils.markups = labels.slice(1);
  return { doc: { ...doc, soils, items }, map };
}
